import {
  type AIModelChoices,
  EvidenceTypeEnum,
  getApiSamplesV1SamplesEvidenceListQueryKey,
  SampleType,
  useApiSamplesV1SamplesEvidenceListSuspense,
  useApiSamplesV1SamplesRetrieveSuspense,
  useQueryClient,
} from 'api';
import {
  CameraDialog,
  NetworkOverrideSwitch,
  NetworkStatusAlert,
  type OfflineProcessUploadResponse,
  type PreviewGalleryImage,
  UploadOptionsButton,
  useManagePreviews,
  useNotification,
  useOfflineEvidence,
  useSiteMaterial,
  useToggleCamera,
} from 'features';
import { useTranslation } from 'i18n';
import { addError } from 'monitor';
import { Suspense, useRef } from 'react';
import { Link, useParams } from 'react-router-dom';
import { ActionRow, Box, Button, Grid, ListSkeletonLoader, Page, PhotoFilePicker, Section, useBreakpoint } from 'ui';

import { SampleDetail } from '../../components/SampleDetail/SampleDetail';
import { SiteHeader } from '../../components/SiteHeader/SiteHeader';

export const Sample = () => {
  const { t } = useTranslation('orders');
  const { sampleId } = useParams();
  const queryClient = useQueryClient();
  const { data: siteMaterial } = useSiteMaterial();
  const parentId = Number(sampleId);
  const { data: sample } = useApiSamplesV1SamplesRetrieveSuspense(parentId);
  const { data: evidence } = useApiSamplesV1SamplesEvidenceListSuspense(parentId, {
    page_size: 10000,
  });
  const { isCameraOpen, toggle } = useToggleCamera();
  const { isMobile } = useBreakpoint();
  const { open } = useNotification();
  const filePickerRef = useRef<HTMLInputElement>(null);

  const evidenceType = sample.type === SampleType.AG ? EvidenceTypeEnum.AS : EvidenceTypeEnum.SB;

  const { mutateAsync: uploadEvidence, removeEvidenceFile } = useOfflineEvidence({
    mode: 'sample',
    mutationOptions: {
      onError: (error, { photo }) => {
        open({
          message: t('samplePage.notifications.failedToUpload', {
            values: { error: String(error), photoName: photo.name },
          }),
          type: 'error',
        });
      },
      onSuccess: () => {
        queryClient.invalidateQueries({
          queryKey: getApiSamplesV1SamplesEvidenceListQueryKey(parentId, { page_size: 10000 }),
        });
      },
    },
    parentId: parentId,
  });

  const {
    previewImages,
    addImage,
    updateOfflineImage,
    updateOnlineImage,
    resetSelectedImages,
    handleDeletePreviewImage,
  } = useManagePreviews({
    mode: 'sample',
    onDeleteSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: getApiSamplesV1SamplesEvidenceListQueryKey(parentId, { page_size: 10000 }),
      });
    },
    parentId: parentId,
  });

  const handleCapture = async (photo: File) => {
    const addedImage = await addImage({
      photo,
    });

    const result = await uploadEvidence({
      evidenceType,
      parentId: String(parentId),
      photo,
      startTime: Date.now(),
    });

    if ('model_results' in result && result.model_results) {
      await updateOnlineImage({
        accepted: true,
        aiModel: (result.model_results?.model_name as AIModelChoices) ?? undefined,
        aluminiumComposition: result.aluminium_composition ?? undefined,
        annotations: [...(result.model_results?.coco_prediction ?? [])],
        evaluatedSpecs: result.evaluated_specs ?? undefined,
        imageRejectionReasons: [],
        imageWarningReasons: [],
        previewImage: addedImage,
        targetWeightPercentage: result.target_weight_percentage ?? undefined,
        url: (typeof result.image === 'string' ? result.image : result.image.url) || addedImage.src,
        warning: false,
      });
    } else {
      updateOfflineImage({
        id: (result as OfflineProcessUploadResponse).id,
        previewImage: addedImage,
      });
    }
  };

  const handleFileSelect = () => {
    filePickerRef.current?.click();
  };

  const deletePreviewImage = (image: PreviewGalleryImage) => {
    handleDeletePreviewImage({
      evidence: evidence?.results || [],
      image,
      removeEvidenceFile,
    });
  };

  const handleCompletePreviewSession = () => {
    resetSelectedImages();

    if (!siteMaterial?.material_spec.ai_model) {
      addError(new Error(t('samplePage.errors.siteMaterialOrAiModelNotFound')), {
        parentId,
        siteMaterial,
      });
      return;
    }

    toggle({ aiModel: siteMaterial?.material_spec.ai_model, sessionId: parentId });
  };

  return (
    <>
      <Page>
        <Page.Title title={t('samplePage.pageTitle')} />
        <Page.Divider />
        <Grid container>
          <Grid
            size={{
              md: 4,
              xs: 12,
            }}
          >
            <Section title={t('samplePage.sections.siteDetails')}>
              <SiteHeader siteMaterial={siteMaterial} />
            </Section>
          </Grid>
          <Grid
            size={{
              md: 8,
              xs: 12,
            }}
          >
            <Section
              title={t('samplePage.sections.sampleDetails')}
              action={
                <ActionRow>
                  <NetworkOverrideSwitch />
                  <Box
                    sx={{
                      flexDirection: isMobile ? 'column' : 'row',
                      display: 'flex',
                      gap: 2,
                    }}
                  >
                    <Button size="large" fullWidth variant="secondary" component={Link} to="edit">
                      {t('samplePage.actions.edit')}
                    </Button>
                    <UploadOptionsButton onUpload={handleFileSelect} onOpenCamera={toggle} isMobile={isMobile} />
                  </Box>
                </ActionRow>
              }
            >
              <NetworkStatusAlert />
              <Suspense fallback={<ListSkeletonLoader />}>
                <SampleDetail
                  sampleId={parentId}
                  evidence={evidence}
                  onSuccessfulEvidenceSync={() => {
                    queryClient.invalidateQueries({
                      queryKey: getApiSamplesV1SamplesEvidenceListQueryKey(parentId, { page_size: 10000 }),
                    });
                  }}
                />
              </Suspense>
            </Section>
          </Grid>
        </Grid>
      </Page>
      <CameraDialog
        isCameraOpen={isCameraOpen}
        toggle={toggle}
        cameraProps={{
          aiModel: siteMaterial?.material_spec.ai_model ?? 'alu',
          handleCapture,
          handleClose: toggle,
          handleDeletePreviewImage: deletePreviewImage,
          handleOnComplete: handleCompletePreviewSession,
          previewImages,
        }}
      />
      <PhotoFilePicker
        ref={filePickerRef}
        onSelectFiles={(photos) => photos.forEach((photo) => handleCapture(photo))}
        hide
        multiple
      />
    </>
  );
};
